import React, { useState, useEffect, useMemo } from 'react';
import { Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './TableGuide.module.css';

/**
 * TableGuide - A standalone interactive guide component for data tables
 * 
 * @param {Object} props Component props
 * @param {function} props.onClose Function called when guide is closed
 * @param {Object} props.tableRef Reference to the table container element
 * @param {Array} props.customSteps Optional custom guide steps to override defaults
 * @returns {React.ReactElement} TableGuide component
 */
const TableGuide = ({ onClose, tableRef, customSteps = null }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  // Default guide steps if custom steps aren't provided
  const defaultGuideSteps = useMemo(() => [
    {
      title: 'Table Guide',
      text: 'This guide will help you understand how to use thistable effectively.',
      element: null,
    },
    {
      title: 'Search loaded data',
      text: 'Click on the magnifiner icon to quickly filter the table data by any value across all columns.',
      element: '.mrt-input',
      position: { top: 20, left: 200 }
    },
    {
      title: 'Column Visibility',
      text: 'Top right of the table, click those button to show or hide specific columns in the table.',
      element: '.mrt-button.mrt-dropdown-button',
      position: { top: 0, right: 80 }
    },
    {
      title: 'Sorting',
      text: 'Click on column headers to sort data. Click again to reverse the sort order.',
      element: '.mrt-thead-cell',
      position: { top: 60, left: 100 }
    },
    {
      title: 'Pagination',
      text: 'Bottom right of the table, use these controls to navigate through pages of data. You can also change how many rows are displayed per page.',
      element: '.mrt-table-bottom-toolbar',
      position: { bottom: 20, left: '50%' }
    },
    {
      title: 'Row Selection',
      text: 'Click checkboxes to select rows for bulk actions.',
      element: '.mrt-row-select-checkbox',
      position: { top: 100, left: 30 }
    }
  ], []);

  // Use custom steps or fall back to default steps
  const guideSteps = useMemo(() => customSteps || defaultGuideSteps, [customSteps, defaultGuideSteps]);

  useEffect(() => {
    const positionTooltip = () => {
      const step = guideSteps[currentStep];
      if (!step.element || !tableRef.current) return;

      const element = tableRef.current.querySelector(step.element);
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const tableRect = tableRef.current.getBoundingClientRect();

      let newPosition = {};
      if (step.position.top !== undefined) {
        newPosition.top = (typeof step.position.top === 'string') 
          ? step.position.top 
          : (rect.top - tableRect.top + step.position.top);
      }
      if (step.position.left !== undefined) {
        newPosition.left = (typeof step.position.left === 'string')
          ? step.position.left
          : (rect.left - tableRect.left + step.position.left);
      }
      if (step.position.right !== undefined) {
        newPosition.right = (typeof step.position.right === 'string')
          ? step.position.right
          : (tableRect.right - rect.right + step.position.right);
        newPosition.left = 'auto';
      }
      if (step.position.bottom !== undefined) {
        newPosition.bottom = (typeof step.position.bottom === 'string')
          ? step.position.bottom
          : (tableRect.bottom - rect.bottom + step.position.bottom);
        newPosition.top = 'auto';
      }

      setTooltipPosition(newPosition);
    };

    if (currentStep === 0) {
      // Center the first tooltip
      setTooltipPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      });
    } else {
      positionTooltip();
    }

    // Reposition tooltip on window resize
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [currentStep, guideSteps, tableRef]);

  const handleNext = () => {
    if (currentStep < guideSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = guideSteps[currentStep];

  return (
    <>
      <div className={styles.guideOverlay} />
      <div 
        className={styles.guideTooltip} 
        style={{
          ...tooltipPosition,
          transform: tooltipPosition.transform || 'none'
        }}
      >
        <IconButton 
          size="small" 
          style={{ position: 'absolute', top: 5, right: 5 }}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        
        <Typography className={styles.guideTitle}>{step.title}</Typography>
        <Typography className={styles.guideText}>{step.text}</Typography>
        
        <div className={styles.guideButtonContainer}>
          <button 
            className={styles.guideDismissButton} 
            onClick={onClose}
          >
            Skip guide
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {currentStep > 0 && (
              <button 
                className={styles.guideBackButton} 
                onClick={handleBack}
              >
                Back
              </button>
            )}
            <button 
              className={styles.guideNextButton} 
              onClick={handleNext}
            >
              {currentStep < guideSteps.length - 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </div>
        
        <div className={styles.guideProgress}>
          {guideSteps.map((_, index) => (
            <div 
              key={index} 
              className={`${styles.guideDot} ${index === currentStep ? styles.guideActiveDot : ''}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default TableGuide;